import AiLabel from "@/components/ai-label";
import { Content } from "@/services/content/types";
import { createImageSizes, createImageUrl } from "@/utils/image";
import { prefixSlug } from "@/utils/url";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import TeaserByLineImage from "../teaser-byline-image";
import TeaserHeading from "../teaser-heading";

const Article = ({
  article,
  compactMode = false,
  darkMode = false,
  fontWeight = "font-semibold",
  highlighted,
  hideImage = false,
  teaserLayoutType,
  interviewMode = false,
}: {
  article: Content.Article;
  compactMode?: boolean;
  darkMode?: boolean;
  fontWeight?: string;
  highlighted?: string;
  hideImage?: boolean;
  teaserLayoutType?: string;
  interviewMode?: boolean;
}) => {
  const title = article.fields.teaserTitle || article.fields.title;
  const image = article.fields.teaserImage || article.fields.images?.[0];
  const author = article.fields.authors?.[0];
  const byLineImage =
    article.fields.authors?.[0]?.fields.image?.fields?.file?.url || false;
  const showByline =
    article.fields.showByline !== undefined ? article.fields.showByline : false;
  const hideBylineImage =
    article.fields.hideBylineImage !== undefined
      ? article.fields.hideBylineImage
      : false;
  const hideThumbnailImage =
    article.fields.hideThumbnailImage !== undefined
      ? article.fields.hideThumbnailImage
      : false;

  return (
    <Link
      href={prefixSlug(article.fields.slug, article.sys.contentType.sys.id)}
      className={classNames(
        "group relative flex gap-2 border-sand-800 !border-b",
        compactMode ? "" : "",
        darkMode ? "!border-static-black-300 !bg-static-black-400 pt-0" : "",
        hideBylineImage
          ? "border-sand-800 !border-b min-h-[80px] md:min-h-[100px] "
          : "",
        highlighted === "Nivå 1(röd)" ? " !border-red-950 border-b" : "",
        highlighted === "Nivå 2(beige)" ? " !border-sand-900 border-b " : "",
        showByline && byLineImage ? "!!border-b" : "",
        interviewMode ? " !border-sand-900" : "",
      )}
    >
      {image?.fields?.file?.url && !hideThumbnailImage && !hideBylineImage && (
        <div
          className={classNames(
            "relative overflow-hidden",
            compactMode
              ? "w-[107px] h-[60px] md:w-[65px] md:h-[37px]"
              : "w-[107px] h-[60px] md:w-[150px] md:h-[84px] ",
            !hideBylineImage ? "md:block" : "block",
          )}
        >
          {image?.fields?.file?.url && (
            <Image
              fill
              src={createImageUrl(image.fields.file.url, {
                crop: "16:9",
              })}
              alt={image.fields.alt || ""}
              className="object-cover"
              sizes={createImageSizes({
                mobile: "12vw",
                tablet: "12vw",
                desktop: "12vw",
              })}
            />
          )}
          <AiLabel image={image} size="sm" />
        </div>
      )}
      <div className={classNames("flex-1", compactMode ? "" : "")}>
        {title && (
          <TeaserHeading
            text={title}
            highlighted={highlighted}
            className={classNames(
              "break-words group-hover:underline pr-2",
              hideBylineImage && byLineImage ? "max-w-[80%]" : "",
              fontWeight ? fontWeight : "font-semibold",
              compactMode ? "text-lg" : " text-lg lg:text-2xl",
            )}
          />
        )}

        {article.fields.excerpt && (
          <span
            className={classNames(
              "block mt-2 text-sm md:text-base",
              darkMode ? "text-white" : "",
              compactMode ? "" : "",
              hideBylineImage && byLineImage ? "max-w-[70%]" : "",
            )}
          >
            {showByline && (
              <span className=" font-bold">{author?.fields.name}: </span>
            )}
            {article.fields.excerpt && <span>{article.fields.excerpt}</span>}
          </span>
        )}
        {author?.fields.image?.fields?.file?.url && hideBylineImage && (
          <TeaserByLineImage
            showbyLineImage={hideBylineImage}
            fileUrl={author?.fields.image?.fields?.file?.url}
            altText=""
            width={"190px"}
            height={"190px"}
            wrapperClassNames={
              compactMode
                ? "max-w-[25%] max-h-[80px] !bottom-0"
                : "!bottom-0 max-h-[90px] md:max-h-[100px] md:max-w-[30%] max-w-[25%]"
            }
          />
        )}
      </div>
    </Link>
  );
};

export default Article;
